import React, { useEffect, useState } from "react";
import AnimationFadeIn from "@components/AnimationFadeIn";
import BackgroundImage from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import { Box, Container, useTheme } from "@mui/material";
import HeroCst from "@components/HeroCst";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ButtonBackPage from "@components/ButtonBackPage";
import ButtonCst from "@components/ButtonCst";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { Level } from "@pages/Contents/Skills/Levels/types";
import LevelRow from "@pages/Contents/Skills/Levels/components/LevelRow/LevelRow";
import {
  ActionsContainer,
  AddLevelButtonContainer,
  ContentContainer,
} from "@pages/Contents/Skills/Levels/style";
import { ServicesURL } from "@utils/utilsApi";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import FormLoadingStateCst from "@components/FormLoadingStateCst";
import useSWRMutation from "swr/mutation";
import { fetcher } from "../../../../services/config";
import { MethodType } from "../../../../services/type";

const Levels = () => {
  const theme = useTheme();
  const { t } = useTranslation(["skills-levels-page", "common"]);
  const { id: corporateId } = useParams();
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message?: string;
  }>();
  const [originalLevels, setOriginalLevels] = useState<Level[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [disableEditing, setDisableEditing] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLevels((current) =>
      current.map((level) => ({
        ...level,
        can_edit: !originalLevels.find((og) => og.id === level.id),
      })),
    );
  }, [originalLevels]);

  const onboardingLevels = [
    { label: "Base", value: "BASIC" },
    { label: "Intermedio", value: "INTERMEDIATE" },
    { label: "Avanzato", value: "ADVANCED" },
  ];

  const { dataDetail: levelsListRes, isLoading: isFetchingLevels } =
    useFetchDataDetail(
      `${ServicesURL.getLevels.replace(":idCorporate", corporateId ?? "1")}`,
    );

  const { trigger: updateLevels } = useSWRMutation(
    `${ServicesURL.postLevel.replace(":idCorporate", corporateId ?? "1")}`,
    (url: string, arg: { arg: any }) =>
      fetcher(`${url}`, MethodType.POST, { ...arg.arg }),
  );

  useEffect(() => {
    if (levelsListRes?.output?.levels) {
      let levels = levelsListRes?.output?.levels;
      if (levels.length === 0)
        levels = [
          {
            id: null,
            identifier: "",
            onboarding_level: "BASIC",
            name: "",
            description: "",
            sort: 1,
            can_edit: true,
          },
        ] as Level[];

      setOriginalLevels(levelsListRes?.output?.levels);
      setLevels(levels);
      setDisableEditing(levelsListRes.output.levels_locked);
    }
  }, [levelsListRes]);

  const reorderList = ({ destination, source }: DropResult) => {
    if (!destination) return;

    const reorderedList = [...levels];
    const [removed] = reorderedList.splice(source.index, 1);

    reorderedList.splice(destination.index, 0, removed);

    setLevels(
      reorderedList.map((level, index) => ({ ...level, sort: index + 1 })),
    );
  };

  const onAddLevel = () => {
    setLevels((current) => [
      ...current,
      {
        identifier: "",
        onboarding_level: "",
        name: "",
        sort: levels.length + 1,
        description: "",
        id: null,
        can_edit: true,
      },
    ]);
  };

  const onEditLevel = (updatedLevel: Level, levelIndex: number) => {
    const updatedLevels = [...levels];
    updatedLevels[levelIndex] = updatedLevel;
    setLevels(updatedLevels);
  };

  const onDeleteLevel = (levelIndex: number) => {
    setLevels((current) => current.filter((_, index) => index !== levelIndex));
  };

  const resetList = () => {
    setLevels(originalLevels);
  };

  const onConfirm = async () => {
    if (validateForm()) {
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });

      try {
        updateLevels({
          levels: levels.map((level, index) => ({ ...level, sort: index + 1 })),
        })
          .then(() => {
            setOpenToast({ s: StatusEnum.OK, message: "" });
          })
          .catch((error) => {
            console.error(error);
            setOpenToast(undefined);
          });
      } catch (error) {
        setOpenToast(undefined);
      }
    }
  };

  const validateForm = () => {
    const requiredFields = ["name", "identifier", "onboarding_level"];
    let errors: string[] = [];

    levels.forEach((level) => {
      Object.keys(level).forEach((key) => {
        if (requiredFields.includes(key) && !level[key as keyof Level])
          errors.push(`${key}_${level.id}`);
      });
    });

    if (errors && errors.length > 0) {
      setOpenToast({
        s: StatusEnum.KO,
        message: t("missing-required-fields") ?? "",
      });
    }

    setErrors(errors);
    return errors.length === 0;
  };

  return (
    <AnimationFadeIn>
      <BackgroundImage
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-selfsignup-form.jpg"
        position={"absolute"}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("hero-title")} description={t("hero-description")} />
        <Box
          sx={{
            mt: "25px",
          }}
        ></Box>
        <ButtonBackPage />

        {isFetchingLevels ? (
          <FormLoadingStateCst />
        ) : (
          <ContentContainer theme={theme}>
            <Box
              sx={{
                width: "100%",
              }}
            >
              <DragDropContext onDragEnd={reorderList}>
                <Droppable droppableId={`droppable_step_area`}>
                  {(provided) => (
                    <Box ref={provided.innerRef} {...provided.droppableProps}>
                      {levels.map((level, levelIndex) => {
                        return (
                          <Draggable
                            isDragDisabled={levels.length === 1}
                            key={`level_${level.id}_draggable`}
                            draggableId={`level_${level.id}`}
                            index={levelIndex}
                          >
                            {(provided) => (
                              <Box
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                              >
                                <LevelRow
                                  key={`level_${level.id}_row`}
                                  levelIndex={levelIndex}
                                  level={level}
                                  errors={errors}
                                  onboardingLevels={onboardingLevels}
                                  onChange={(updatedLevel) =>
                                    onEditLevel(updatedLevel, levelIndex)
                                  }
                                  totalLevels={levels.length}
                                  dragHandleProps={provided.dragHandleProps}
                                  onDelete={() => onDeleteLevel(levelIndex)}
                                  enableEditing={!disableEditing}
                                />
                              </Box>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            </Box>

            <AddLevelButtonContainer>
              <ButtonCst
                id={"back-button"}
                variant={"contained"}
                size={"medium"}
                disabled={disableEditing}
                sx={{
                  minWidth: "150px",
                  [theme.breakpoints.down("lg")]: {
                    minWidth: "100px",
                  },
                  [theme.breakpoints.down("sm")]: {
                    minWidth: "100%",
                  },
                }}
                onClick={onAddLevel}
              >
                {t("add-new-cta")}
              </ButtonCst>
            </AddLevelButtonContainer>

            <ActionsContainer theme={theme}>
              <ButtonCst
                id={"back-button"}
                variant={"outlined"}
                size={"medium"}
                sx={{
                  alignSelf: "end",
                  minWidth: "150px",
                  mr: "auto",
                  [theme.breakpoints.down("lg")]: {
                    minWidth: "100px",
                  },
                  [theme.breakpoints.down("sm")]: {
                    minWidth: "100%",
                  },
                }}
                onClick={resetList}
                disabled={disableEditing || levels.length === 0}
              >
                {t("reset-cta")}
              </ButtonCst>

              <ButtonCst
                id={"back-button"}
                variant={"contained"}
                size={"medium"}
                sx={{
                  alignSelf: "end",
                  minWidth: "150px",
                  mr: "auto",
                  [theme.breakpoints.up("sm")]: {
                    minWidth: "100px",
                    ml: 3,
                  },
                  [theme.breakpoints.down("sm")]: {
                    minWidth: "100%",
                    ml: 0,
                    mt: 1,
                  },
                }}
                onClick={onConfirm}
                disabled={disableEditing || levels.length === 0}
              >
                {t("submit-cta")}
              </ButtonCst>
            </ActionsContainer>
          </ContentContainer>
        )}
      </Container>

      <ModalConfirmsCst
        open={!!openToast}
        title={t("hero-title")}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        callBackConfirmed={() => {
          setOpenToast(undefined);
          navigate(-1);
        }}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default Levels;
